<template>
  <div
    :class="[
      'tw-max-w-lg',
      'tw-flex tw-flex-col tw-gap-3 tw-p-5',
      'tw-border tw-border-solid tw-border-gray-200',
      'tw-rounded-lg tw-shadow-lg tw-bg-gray-50',
      'tw-text-gray-700'
    ]"
  >
    <div class="tw-flex tw-flex-col tw-gap-1">
      <!-- Name -->
      <div>
        <input
          v-if="editing.name && canEdit"
          ref="nameInput"
          v-model="task.name"
          type="text"
          :class="[
            'tw-w-full tw-px-3 tw-py-1.5',
            'tw-border tw-border-solid tw-border-gray-300',
            'tw-rounded-md tw-bg-white',
            'tw-font-bold tw-text-gray-800',
          ]"
          @blur="editing.name = false"
        >
        <h3
          v-else
          :class="[
            'tw-text-sm tw-font-bold tw-text-gray-700',
            'tw-cursor-pointer'
          ]"
          @click="editing.name = true"
        >
          {{ task.name || 'Click to add task name' }}
        </h3>
      </div>

      <!-- Description -->
      <div>
        <textarea
          v-if="editing.description && canEdit"
          ref="descInput"
          v-model="task.description"
          :class="[
            'tw-w-full tw-resize-y tw-h-24 tw-px-3 tw-py-1.5',
            'tw-border tw-border-solid tw-border-gray-300 tw-rounded-md',
            'tw-bg-white',
            'tw-text-sm tw-text-gray-500',
          ]"
          spellcheck="false"
          @blur="editing.description = false"
        />
        <p
          v-else
          :class="[
            'tw-text-sm tw-text-gray-500',
            'tw-cursor-pointer',
          ]"
          @click="editing.description = true"
        >
          {{ task.description || 'Click to add description' }}
        </p>
      </div>
    </div>


    <!-- Other Options -->
    <div class="tw-flex tw-gap-3 tw-items-start other-options">
      <!-- Due Date -->
      <div>
        <label>Due Date</label>
        <input
          v-model="task.due_date"
          type="date"
          class="date"
          :disabled="!canEdit"
        >
        <p
          v-if="errors.due_date"
          class="error"
        >
          {{ errors.due_date }}
        </p>
      </div>

      <!-- Assignee -->
      <div>
        <div>
          <label>Assign to Contact</label>
          <select
            class="dropdown"
            :multiple="allowMultipleAssignees"
            :disabled="!canEdit"
            @change="handleAssigneeChange"
          >
            <option
              v-for="user in users"
              :key="user.id"
              :value="user.id"
              :selected="user.id === task.assignee || task.assignees?.includes(user.id)"
            >
              {{ user.name }}
            </option>
          </select>
        </div>

        <!-- Team (Wealthbox only) -->
        <div v-if="crm === 'Wealthbox'">
          <label>Assign to Team</label>
          <select
            class="dropdown"
            :disabled="!canEdit"
            @change="handleTeamChange"
          >
            <option
              v-for="team in teams"
              :key="team.id"
              :value="team.id"
              :selected="team.id === task.team || task.teams?.includes(team.id)"
            >
              {{ team.name }}
            </option>
          </select>
        </div>
      </div>
    </div>

    <div class="tw-flex tw-gap-3 tw-items-center tw-justify-between">
      <!-- Sync to CRM -->
      <div
        :class="[
          'tw-flex tw-items-center tw-gap-2',
          task.crm_sync ? '' : 'tw-opacity-70'
        ]"
      >
        <!-- Icon Version
        <i
          v-if="!task.crm_id"
          :class="[
            'fa tw-text-2xl',
            `fa-toggle-${task.crm_sync ? 'on' : 'off'}`,
            task.crm_id ? '' : 'tw-cursor-pointer'
          ]"
          @click="toggleSync"
        /> -->
        <input
          v-if="!task.crm_id"
          v-model="task.crm_sync"
          type="checkbox"
          class="tw-cursor-pointer tw-mb-0.5"
        >
        <label
          :class="[
            'tw-text-sm',
            task.crm_id ? 'tw-text-gray-400 tw-italic' : ''
          ]"
        >
          {{ task.crm_id ? 'Already pushed' : 'Push to CRM' }}
        </label>
      </div>

      <!-- Delete -->
      <div>
        <button
          class="tw-border-none tw-bg-transparent tw-cursor-pointer tw-text-rose-700"
          title="Delete"
          :disabled="!canEdit"
          @click="deleteTask"
        >
          <i class="fa fa-trash" />
        </button>
      </div>
    </div>
  </div>
</template>


<script setup>
import { ref, computed, watch, defineEmits, defineProps, nextTick, onMounted } from 'vue';


// Props & State
// -------------------------------------------------------------------------------->

const props = defineProps({
  task: {
    type: Object,
    required: true,
  },
  users: {
    type: Array,
    default: () => [],
  },
  teams: {
    type: Array,
    default: () => [],
  },
  crm: {
    type: String,
    default: null,
  },
  crmId: {
    type: [String, Number],
    default: null,
  },
  canEdit: {
    type: Boolean,
    default: false,
  },
});

const task = ref({ ...props.task });

const editing = ref({
  name: false,
  description: false,
});
const nameInput = ref(null);
const descInput = ref(null);
const errors = ref({});

const allowMultipleAssignees = computed(() => {
  return props.crm === 'Redtail';
});


// Regular Methods
// -------------------------------------------------------------------------------->

// Soft delete task
const deleteTask = () => {
  task.value = {
    ...task.value,
    deleted: new Date().toISOString(),
  };
};

const handleAssigneeChange = event => {
  const value = event.target.value;

  const assignee = isNaN(parseInt(value)) ? value : parseInt(value);
  task.value.assignee = assignee;

  if (allowMultipleAssignees.value) {
    const assignees = Array.from(event.target.selectedOptions, option => option.value);
    task.value.assignees = assignees.map(id => isNaN(parseInt(id)) ? id : parseInt(id));
  }

  if (assignee) {
    task.value.crm = props.crm;
    task.value.team = null; // Clear team if assigning to user
  }

  // Task watcher doesn't seem reliable without these, even with the deep option.
  task.value = { ...task.value };
};

const handleTeamChange = event => {
  const value = event.target.value;

  const team = isNaN(parseInt(value)) ? value : parseInt(value);
  task.value.team = team;

  if (team) {
    task.value.crm = props.crm;
    task.value.assignee = null; // Clear assignee if assigning to team
  }

  // Task watcher doesn't seem reliable without these, even with the deep option.
  task.value = { ...task.value };
};

// const toggleSync = () => {
//   if (task.value.crm_id) return;
//   task.value.crm_sync = !task.value.crm_sync;
// };

const checkDate = date => {
  let dateError = '';

  const pattern = /^\d{4}-\d{2}-\d{2}$/; // YYYY-MM-DD
  if (date && !pattern.test(date)) {
    dateError = 'Invalid date';
  }

  errors.value = {
    ...errors.value,
    due_date: dateError,
  };
};

const fixAssignee = () => {
  // The assignee value should be the crm id of the user
  const assignee = task.value.assignee;

  if (!assignee) {
    task.value.assignee = null;
    return;
  }

  if (isNaN(parseInt(assignee))) {
    if (typeof assignee !== 'string') {
      task.value.assignee = null;
      return;
    }

    // It's a string
    // Some ids are strings, but it could also be a name.
    const user = props.users.find(user => user.name === assignee);
    if (user) {
      task.value.assignee = user.id;
      return;
    }
  }

  // If we are here, we are assuming it's an id
  // We still want to make sure it's in the user list
  const user = props.users.find(user => user.id === assignee);
  if (!user) task.value.assignee = null;
};


// Watchers
// -------------------------------------------------------------------------------->

watch(task, newValue => {
  emit('updated', newValue);
}, { deep: true });

// Update local task with CRM ID after push
watch(() => props.crmId, newValue => {
  task.value = { ...task.value, crm_id: newValue };
});

// Watch editing state to focus inputs when active
watch(editing, async newValue => {
  await nextTick();

  if (newValue.name && nameInput.value) {
    nameInput.value.focus();
  }

  if (newValue.description && descInput.value) {
    descInput.value.focus();
  }
}, { deep: true });

// Validate due date
watch(() => task.value.due_date, newDate => {
  checkDate(newDate);
});

// When the users load, we need to check if the assignee is valid
watch(() => props.users, () => {
  fixAssignee();
});


// Events
// -------------------------------------------------------------------------------->
const emit = defineEmits(['updated']);


// Lifecycle Hooks
// -------------------------------------------------------------------------------->
onMounted(() => {
  checkDate(task.value.due_date);
});

</script>


<style scoped>
.date, .dropdown {
  @apply tw-px-1.5 tw-py-0.5 tw-w-full;
  @apply tw-border tw-border-solid tw-border-gray-300;
  @apply tw-rounded-lg tw-bg-white;
  @apply tw-text-xs tw-text-gray-800 tw-cursor-pointer;
}

.other-options label {
  @apply tw-text-xs tw-text-gray-600;
}

.error {
  @apply tw-text-xs tw-text-rose-600;
  @apply tw-px-1.5 tw-py-1;
}
</style>
